import React, { useState, useEffect } from 'react';
import {
  CreditCard, Plus, DollarSign, Diamond, Users, ClipboardList,
  Edit2, Trash2, Pause, Play, Gift, BarChart2, Check, X, Save, Package
} from 'lucide-react';
import AdminLayout from '../../components/admin/AdminLayout';
import AdminTable from '../../components/admin/AdminTable';
import AdminCard from '../../components/admin/AdminCard';
import AdminModal from '../../components/admin/AdminModal';
import PlanFeaturesConfig from '../../components/admin/PlanFeaturesConfig';
import api from '../../services/api';
import { useProgram } from '../../contexts/ProgramContext';
import { format } from 'date-fns';
import { ptBR } from 'date-fns/locale';
import '../../styles/pages/admin/SubscriptionsPage.css';
import '../../styles/admin-shared.css';

const SubscriptionsPage = () => {
  // Re-fetch on program switch
  const { slug } = useProgram();
  const [subscriptions, setSubscriptions] = useState([]);
  const [plans, setPlans] = useState([]);
  const [revenueStats, setRevenueStats] = useState(null);
  const [loading, setLoading] = useState(true);
  const [showPlanModal, setShowPlanModal] = useState(false);
  const [editingPlan, setEditingPlan] = useState(null);
  const [formData, setFormData] = useState({
    name: '',
    description: '',
    price_monthly: '',
    price_yearly: '',
    features: '',
    is_active: true,
    trial_days: 0,
    // Todas as configurações de features
    max_groups_join: 5,
    max_challenges_active: 10,
    max_diary_entries: -1,
    storage_limit_mb: 100,
    can_create_groups: false,
    can_create_forum_posts: true,
    max_group_posts_day: 10,
    max_forum_posts_day: 5,
    can_send_private_msg: true,
    max_private_msg_day: 20,
    can_access_premium_content: false,
    can_download_content: false,
    max_content_downloads: 0,
    can_access_meditation: true,
    can_access_premium_meditation: false,
    can_download_meditation: false,
    max_meditation_downloads: 0,
    meditation_ad_free: false,
    earn_points_multiplier: 1.0,
    can_access_leaderboard: true,
    can_appear_leaderboard: true,
    max_achievements_unlock: -1,
    advanced_analytics: false,
    can_export_data: true,
    data_retention_days: 365,
    priority_support: false,
    ai_chat_enabled: false,
    ai_messages_limit: 0,
    custom_themes: false,
    ad_free_experience: false,
    content_blocker_enabled: true,
    advanced_blocker: false,
    custom_blocklist: false,
    emergency_sms_enabled: false,
    emergency_call_enabled: false,
    max_emergency_contacts: 3
  });

  useEffect(() => {
    fetchData();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [slug]);

  const fetchData = async () => {
    setLoading(true);
    try {
      const [plansData, subsData, revenue] = await Promise.all([
        api.get('/admin/subscriptions/plans').catch(() => ({ data: { data: { plans: [] } } })),
        api.get('/admin/subscriptions').catch(() => ({ data: { data: { subscriptions: [] } } })),
        api.get('/admin/revenue/stats').catch(() => ({ data: { data: null } }))
      ]);

      setPlans(plansData.data.data?.plans || []);
      setSubscriptions(subsData.data.data?.subscriptions || []);
      setRevenueStats(revenue.data.data);
    } catch (error) {
      // Erro ao carregar dados - manter silencioso em produção
      setPlans([]);
      setSubscriptions([]);
      setRevenueStats(null);
    } finally {
      setLoading(false);
    }
  };

  const handleOpenModal = (plan = null) => {
    if (plan) {
      setEditingPlan(plan);
      setFormData({
        name: plan.name,
        description: plan.description || '',
        price_monthly: plan.price_monthly,
        price_yearly: plan.price_yearly,
        features: Array.isArray(plan.features) ? plan.features.join('\n') : plan.features || '',
        is_active: plan.is_active ?? true,
        trial_days: plan.trial_days || 0,
        // Carregar todas as features
        max_groups_join: plan.max_groups_join ?? 5,
        max_challenges_active: plan.max_challenges_active ?? 10,
        max_diary_entries: plan.max_diary_entries ?? -1,
        storage_limit_mb: plan.storage_limit_mb ?? 100,
        can_create_groups: plan.can_create_groups ?? false,
        can_create_forum_posts: plan.can_create_forum_posts ?? true,
        max_group_posts_day: plan.max_group_posts_day ?? 10,
        max_forum_posts_day: plan.max_forum_posts_day ?? 5,
        can_send_private_msg: plan.can_send_private_msg ?? true,
        max_private_msg_day: plan.max_private_msg_day ?? 20,
        can_access_premium_content: plan.can_access_premium_content ?? false,
        can_download_content: plan.can_download_content ?? false,
        max_content_downloads: plan.max_content_downloads ?? 0,
        can_access_meditation: plan.can_access_meditation ?? true,
        can_access_premium_meditation: plan.can_access_premium_meditation ?? false,
        can_download_meditation: plan.can_download_meditation ?? false,
        max_meditation_downloads: plan.max_meditation_downloads ?? 0,
        meditation_ad_free: plan.meditation_ad_free ?? false,
        earn_points_multiplier: plan.earn_points_multiplier ?? 1.0,
        can_access_leaderboard: plan.can_access_leaderboard ?? true,
        can_appear_leaderboard: plan.can_appear_leaderboard ?? true,
        max_achievements_unlock: plan.max_achievements_unlock ?? -1,
        advanced_analytics: plan.advanced_analytics ?? false,
        can_export_data: plan.can_export_data ?? true,
        data_retention_days: plan.data_retention_days ?? 365,
        priority_support: plan.priority_support ?? false,
        ai_chat_enabled: plan.ai_chat_enabled ?? false,
        ai_messages_limit: plan.ai_messages_limit ?? 0,
        custom_themes: plan.custom_themes ?? false,
        ad_free_experience: plan.ad_free_experience ?? false,
        content_blocker_enabled: plan.content_blocker_enabled ?? true,
        advanced_blocker: plan.advanced_blocker ?? false,
        custom_blocklist: plan.custom_blocklist ?? false,
        emergency_sms_enabled: plan.emergency_sms_enabled ?? false,
        emergency_call_enabled: plan.emergency_call_enabled ?? false,
        max_emergency_contacts: plan.max_emergency_contacts ?? 3
      });
    } else {
      setEditingPlan(null);
      setFormData({
        name: '',
        description: '',
        price_monthly: '',
        price_yearly: '',
        features: '',
        is_active: true,
        trial_days: 0,
        max_groups_join: 5,
        max_challenges_active: 10,
        max_diary_entries: -1,
        storage_limit_mb: 100,
        can_create_groups: false,
        can_create_forum_posts: true,
        max_group_posts_day: 10,
        max_forum_posts_day: 5,
        can_send_private_msg: true,
        max_private_msg_day: 20,
        can_access_premium_content: false,
        can_download_content: false,
        max_content_downloads: 0,
        can_access_meditation: true,
        can_access_premium_meditation: false,
        can_download_meditation: false,
        max_meditation_downloads: 0,
        meditation_ad_free: false,
        earn_points_multiplier: 1.0,
        can_access_leaderboard: true,
        can_appear_leaderboard: true,
        max_achievements_unlock: -1,
        advanced_analytics: false,
        can_export_data: true,
        data_retention_days: 365,
        priority_support: false,
        ai_chat_enabled: false,
        ai_messages_limit: 0,
        custom_themes: false,
        ad_free_experience: false,
        content_blocker_enabled: true,
        advanced_blocker: false,
        custom_blocklist: false,
        emergency_sms_enabled: false,
        emergency_call_enabled: false,
        max_emergency_contacts: 3
      });
    }
    setShowPlanModal(true);
  };

  const handleSavePlan = async (e) => {
    e.preventDefault();
    try {
      const planData = {
        name: formData.name,
        description: formData.description,
        price_monthly: parseFloat(formData.price_monthly),
        price_yearly: parseFloat(formData.price_yearly),
        trial_days: parseInt(formData.trial_days) || 0,
        features: formData.features.split('\n').filter(f => f.trim()),
        is_active: formData.is_active,
        // Todas as configurações de funcionalidades
        max_groups_join: parseInt(formData.max_groups_join) || 5,
        max_challenges_active: parseInt(formData.max_challenges_active) || 10,
        max_diary_entries: parseInt(formData.max_diary_entries) || -1,
        storage_limit_mb: parseInt(formData.storage_limit_mb) || 100,
        can_create_groups: formData.can_create_groups,
        can_create_forum_posts: formData.can_create_forum_posts,
        max_group_posts_day: parseInt(formData.max_group_posts_day) || 10,
        max_forum_posts_day: parseInt(formData.max_forum_posts_day) || 5,
        can_send_private_msg: formData.can_send_private_msg,
        max_private_msg_day: parseInt(formData.max_private_msg_day) || 20,
        can_access_premium_content: formData.can_access_premium_content,
        can_download_content: formData.can_download_content,
        max_content_downloads: parseInt(formData.max_content_downloads) || 0,
        can_access_meditation: formData.can_access_meditation,
        can_access_premium_meditation: formData.can_access_premium_meditation,
        can_download_meditation: formData.can_download_meditation,
        max_meditation_downloads: parseInt(formData.max_meditation_downloads) || 0,
        meditation_ad_free: formData.meditation_ad_free,
        earn_points_multiplier: parseFloat(formData.earn_points_multiplier) || 1.0,
        can_access_leaderboard: formData.can_access_leaderboard,
        can_appear_leaderboard: formData.can_appear_leaderboard,
        max_achievements_unlock: parseInt(formData.max_achievements_unlock) || -1,
        advanced_analytics: formData.advanced_analytics,
        can_export_data: formData.can_export_data,
        data_retention_days: parseInt(formData.data_retention_days) || 365,
        priority_support: formData.priority_support,
        ai_chat_enabled: formData.ai_chat_enabled,
        ai_messages_limit: parseInt(formData.ai_messages_limit) || 0,
        custom_themes: formData.custom_themes,
        ad_free_experience: formData.ad_free_experience,
        content_blocker_enabled: formData.content_blocker_enabled,
        advanced_blocker: formData.advanced_blocker,
        custom_blocklist: formData.custom_blocklist,
        emergency_sms_enabled: formData.emergency_sms_enabled,
        emergency_call_enabled: formData.emergency_call_enabled,
        max_emergency_contacts: parseInt(formData.max_emergency_contacts) || 3
      };

      console.log('Saving plan with data:', planData);

      let response;
      if (editingPlan) {
        response = await api.put(`/admin/subscriptions/plans/${editingPlan.id}`, planData);
      } else {
        response = await api.post('/admin/subscriptions/plans', planData);
      }
      
      console.log('Plan saved successfully:', response.data);
      
      setShowPlanModal(false);
      setEditingPlan(null);
      fetchData();
    } catch (error) {
      console.error('Erro ao salvar plano:', error);
      alert(error.response?.data?.message || 'Erro ao salvar plano');
    }
  };

  const handleDeletePlan = async (planId) => {
    if (!window.confirm('Deseja realmente excluir este plano?')) return;
    
    try {
      await api.delete(`/admin/subscriptions/plans/${planId}`);
      fetchData();
    } catch (error) {
      console.error('Erro ao excluir plano:', error);
      alert(error.response?.data?.message || 'Erro ao excluir plano');
    }
  };

  const handleTogglePlanStatus = async (planId, currentStatus) => {
    try {
      await api.patch(`/admin/subscriptions/plans/${planId}/status`, {
        is_active: !currentStatus
      });
      fetchData();
    } catch (error) {
      console.error('Erro ao alterar status:', error);
      alert(error.response?.data?.message || 'Erro ao alterar status do plano');
    }
  };

  if (loading) {
    return (
      <AdminLayout>
        <div className="loading-container">
          <div className="spinner"></div>
          <p>Carregando dados...</p>
        </div>
      </AdminLayout>
    );
  }

  const columns = [
    {
      key: 'user',
      label: 'Usuário',
      render: (value, row) => (
        <div>
          <div className="user-name">{row.users?.name || 'N/A'}</div>
          <div className="user-email">{row.users?.email || ''}</div>
        </div>
      )
    },
    {
      key: 'plan',
      label: 'Plano',
      render: (value, row) => row.subscription_plans?.name || 'N/A'
    },
    {
      key: 'status',
      label: 'Status',
      render: (value) => {
        const statusMap = {
          ACTIVE: { label: 'Ativo', class: 'success' },
          CANCELED: { label: 'Cancelado', class: 'danger' },
          EXPIRED: { label: 'Expirado', class: 'warning' },
          TRIAL: { label: 'Trial', class: 'info' }
        };
        const status = statusMap[value] || { label: value, class: 'default' };
        return (
          <span className={`badge badge-${status.class}`}>
            {status.label}
          </span>
        );
      }
    },
    {
      key: 'billing_cycle',
      label: 'Ciclo',
      render: (value) => value === 'MONTHLY' ? 'Mensal' : 'Anual'
    },
    {
      key: 'current_period_end',
      label: 'Próxima Cobrança',
      render: (value) => value ? format(new Date(value), 'dd/MM/yyyy', { locale: ptBR }) : '-'
    },
    {
      key: 'created_at',
      label: 'Início',
      render: (value) => format(new Date(value), 'dd/MM/yyyy', { locale: ptBR })
    }
  ];

  return (
    <AdminLayout>
      <div className="subscriptions-page">
        {/* Header */}
        <div className="page-header">
          <div>
            <h1><CreditCard size={26} /> Assinaturas & Receita</h1>
            <p>Gerencie planos de assinatura e acompanhe a receita da plataforma</p>
          </div>
          <button
            className="btn btn-primary"
            onClick={() => handleOpenModal()}
          >
            <Plus size={16} /> Criar Novo Plano
          </button>
        </div>

        {/* Revenue Cards */}
        <div className="analytics-cards-grid">
          <AdminCard
            title="MRR (Receita Mensal Recorrente)"
            value={`R$ ${(revenueStats?.mrr || 0).toLocaleString('pt-BR', { minimumFractionDigits: 2 })}`}
            subtitle="Receita mensal recorrente"
            icon={<DollarSign size={22} />}
            color="green"
            trend={revenueStats?.mrrGrowth}
          />
          <AdminCard
            title="ARR (Receita Anual Recorrente)"
            value={`R$ ${(revenueStats?.arr || 0).toLocaleString('pt-BR', { minimumFractionDigits: 2 })}`}
            subtitle="Receita anual projetada"
            icon={<BarChart2 size={22} />}
            color="blue"
          />
          <AdminCard
            title="Assinaturas Ativas"
            value={subscriptions.filter(s => s.status === 'ACTIVE').length}
            subtitle={`Total: ${subscriptions.length} assinaturas`}
            icon={<Users size={22} />}
            color="purple"
          />
          <AdminCard
            title="Planos Disponíveis"
            value={plans.filter(p => p.is_active).length}
            subtitle={`${plans.length} planos criados`}
            icon={<ClipboardList size={22} />}
            color="yellow"
          />
        </div>

        {/* Plans Section */}
        <div className="plans-section">
          <div className="section-header">
            <h2><Package size={20} /> Planos Disponíveis no App</h2>
            <p className="section-subtitle">
              {plans.filter(p => p.is_active).length} plano(s) ativo(s) • 
              Visíveis para usuários no aplicativo mobile
            </p>
          </div>
          
          {plans.length === 0 ? (
            <div className="empty-state">
              <Package size={40} className="empty-icon" />
              <p>Nenhum plano criado ainda</p>
              <button className="btn btn-primary" onClick={() => handleOpenModal()}>
                <Plus size={16} /> Criar Primeiro Plano
              </button>
            </div>
          ) : (
            <div className="plans-grid">
              {plans.map(plan => (
                <div key={plan.id} className={`plan-card ${!plan.is_active ? 'inactive' : ''}`}>
                  <div className="plan-header">
                    <h3>{plan.name}</h3>
                    <div className="plan-status">
                      {plan.is_active ? (
                        <span className="status-badge active"><Check size={12} /> Ativo</span>
                      ) : (
                        <span className="status-badge inactive"><X size={12} /> Inativo</span>
                      )}
                    </div>
                  </div>
                  
                  <div className="plan-description">
                    {plan.description || 'Sem descrição'}
                  </div>
                  
                  <div className="plan-pricing">
                    <div className="price-item">
                      <span className="price-label">Mensal</span>
                      <span className="price-value">R$ {parseFloat(plan.price_monthly).toFixed(2)}</span>
                    </div>
                    <div className="price-item">
                      <span className="price-label">Anual</span>
                      <span className="price-value">R$ {parseFloat(plan.price_yearly).toFixed(2)}</span>
                      <span className="price-save">
                        Economize {((1 - (plan.price_yearly / (plan.price_monthly * 12))) * 100).toFixed(0)}%
                      </span>
                    </div>
                  </div>

                  {plan.trial_days > 0 && (
                    <div className="plan-trial">
                      <Gift size={14} /> {plan.trial_days} dias de teste grátis
                    </div>
                  )}

                  {plan.features && plan.features.length > 0 && (
                    <div className="plan-features">
                      <strong>Recursos:</strong>
                      <ul>
                        {(Array.isArray(plan.features) ? plan.features : []).map((feature, idx) => (
                          <li key={idx}><Check size={12} /> {feature}</li>
                        ))}
                      </ul>
                    </div>
                  )}

                  <div className="plan-actions">
                    <button
                      className="btn btn-sm btn-outline"
                      onClick={() => handleOpenModal(plan)}
                      title="Editar plano"
                    >
                      <Edit2 size={13} /> Editar
                    </button>
                    <button
                      className={`btn btn-sm ${plan.is_active ? 'btn-warning' : 'btn-success'}`}
                      onClick={() => handleTogglePlanStatus(plan.id, plan.is_active)}
                      title={plan.is_active ? 'Desativar' : 'Ativar'}
                    >
                      {plan.is_active ? <><Pause size={13} /> Desativar</> : <><Play size={13} /> Ativar</>}
                    </button>
                    <button
                      className="btn btn-sm btn-danger"
                      onClick={() => handleDeletePlan(plan.id)}
                      title="Excluir plano"
                    >
                      <Trash2 size={13} /> Excluir
                    </button>
                  </div>
                </div>
              ))}
            </div>
          )}
        </div>

        {/* Subscriptions Table */}
        <div className="subscriptions-section">
          <h2><BarChart2 size={20} /> Assinaturas Ativas</h2>
          <AdminTable
            columns={columns}
            data={subscriptions}
            loading={loading}
            actions={(row) => (
              <button
                className="table-action-btn delete"
                onClick={async () => {
                  if (window.confirm('Cancelar esta assinatura?')) {
                    try {
                      await api.post(`/admin/subscriptions/${row.id}/cancel`);
                      fetchData();
                    } catch (error) {
                      alert('Erro ao cancelar assinatura');
                    }
                  }
                }}
                title="Cancelar assinatura"
              >
                <X size={14} /> Cancelar
              </button>
            )}
          />
        </div>

        {/* Plan Modal */}
        <AdminModal
          isOpen={showPlanModal}
          onClose={() => {
            setShowPlanModal(false);
            setEditingPlan(null);
            setFormData({
              name: '',
              description: '',
              price_monthly: '',
              price_yearly: '',
              features: '',
              is_active: true,
              trial_days: 0,
              max_groups_join: 5,
              max_challenges_active: 10,
              max_diary_entries: -1,
              storage_limit_mb: 100,
              can_create_groups: false,
              can_create_forum_posts: true,
              max_group_posts_day: 10,
              max_forum_posts_day: 5,
              can_send_private_msg: true,
              max_private_msg_day: 20,
              can_access_premium_content: false,
              can_download_content: false,
              max_content_downloads: 0,
              can_access_meditation: true,
              can_access_premium_meditation: false,
              can_download_meditation: false,
              max_meditation_downloads: 0,
              meditation_ad_free: false,
              earn_points_multiplier: 1.0,
              can_access_leaderboard: true,
              can_appear_leaderboard: true,
              max_achievements_unlock: -1,
              advanced_analytics: false,
              can_export_data: true,
              data_retention_days: 365,
              priority_support: false,
              ai_chat_enabled: false,
              ai_messages_limit: 0,
              custom_themes: false,
              ad_free_experience: false,
              content_blocker_enabled: true,
              advanced_blocker: false,
              custom_blocklist: false,
              emergency_sms_enabled: false,
              emergency_call_enabled: false,
              max_emergency_contacts: 3
            });
          }}
          title={editingPlan ? <><Edit2 size={20} /> Editar Plano</> : <><Plus size={20} /> Criar Novo Plano</>}
        >
          <form onSubmit={handleSavePlan} className="plan-form">
            <div className="form-group">
              <label htmlFor="name">Nome do Plano *</label>
              <input
                type="text"
                id="name"
                name="name"
                value={formData.name}
                onChange={(e) => setFormData({ ...formData, name: e.target.value })}
                placeholder="Ex: Premium, Pro, Básico"
                required
              />
            </div>

            <div className="form-group">
              <label htmlFor="description">Descrição</label>
              <textarea
                id="description"
                name="description"
                value={formData.description}
                onChange={(e) => setFormData({ ...formData, description: e.target.value })}
                placeholder="Descreva os benefícios deste plano..."
                rows="3"
              />
            </div>

            <div className="form-row">
              <div className="form-group">
                <label htmlFor="price_monthly">Preço Mensal (R$) *</label>
                <input
                  type="number"
                  id="price_monthly"
                  name="price_monthly"
                  value={formData.price_monthly}
                  onChange={(e) => setFormData({ ...formData, price_monthly: e.target.value })}
                  placeholder="29.90"
                  step="0.01"
                  min="0"
                  required
                />
              </div>

              <div className="form-group">
                <label htmlFor="price_yearly">Preço Anual (R$) *</label>
                <input
                  type="number"
                  id="price_yearly"
                  name="price_yearly"
                  value={formData.price_yearly}
                  onChange={(e) => setFormData({ ...formData, price_yearly: e.target.value })}
                  placeholder="299.00"
                  step="0.01"
                  min="0"
                  required
                />
              </div>
            </div>

            <div className="form-group">
              <label htmlFor="trial_days">Período de Trial (dias)</label>
              <input
                type="number"
                id="trial_days"
                name="trial_days"
                value={formData.trial_days}
                onChange={(e) => setFormData({ ...formData, trial_days: e.target.value })}
                placeholder="7"
                min="0"
              />
              <small className="form-hint">Deixe 0 para não oferecer período de teste</small>
            </div>

            {/* Configuração de Funcionalidades */}
            <div className="form-group">
              <label>Funcionalidades do Plano</label>
              <small className="form-hint" style={{ display: 'block', marginBottom: '10px' }}>
                Configure quais recursos estarão disponíveis neste plano. Use -1 para ilimitado.
              </small>
              <PlanFeaturesConfig
                features={formData}
                onChange={setFormData}
              />
            </div>

            <div className="form-group">
              <label htmlFor="features">Resumo dos Recursos (um por linha)</label>
              <textarea
                id="features"
                name="features"
                value={formData.features}
                onChange={(e) => setFormData({ ...formData, features: e.target.value })}
                placeholder="Acesso completo à comunidade&#10;Chat ilimitado&#10;Conteúdo educativo premium&#10;Suporte prioritário"
                rows="4"
              />
              <small className="form-hint">Resumo visual que aparece no card do plano (opcional)</small>
            </div>

            <div className="form-group checkbox-group">
              <label className="checkbox-label">
                <input
                  type="checkbox"
                  name="is_active"
                  checked={formData.is_active}
                  onChange={(e) => setFormData({ ...formData, is_active: e.target.checked })}
                />
                <span>Plano ativo (visível no aplicativo)</span>
              </label>
              <small className="form-hint">
                Apenas planos ativos são exibidos para os usuários no app mobile
              </small>
            </div>

            <div className="modal-actions">
              <button 
                type="button" 
                className="btn btn-outline"
                onClick={() => {
                  setShowPlanModal(false);
                  setEditingPlan(null);
                }}
              >
                Cancelar
              </button>
              <button type="submit" className="btn btn-primary">
                {editingPlan ? <><Save size={16} /> Salvar Alterações</> : <><Plus size={16} /> Criar Plano</>}
              </button>
            </div>
          </form>
        </AdminModal>
      </div>
    </AdminLayout>
  );
};

export default SubscriptionsPage;
